import { VelavuRule, VelavuRuleDeviceConfig } from "velavu-js-api";
import { NormalizedRule } from "./normalized/normalized-rule";
import { useMemo, useState } from "react";
import { RulesInputState } from "./input/rules-input-state";
import {
	makeDefaultRulesEditorErrorState,
	RulesEditorErrorState,
	RulesEditorEventErrorState,
} from "./input/rules-editor-error-state";
import {
	RulesEventInputState,
	rulesEventInputStateFromEventOptions,
} from "./input/rules-event-input-state";
import NormalizedRuleEventOptions from "./normalized/normalized-rule-event-options";
import {
	isValidE164USPhoneNumber,
	isValidEmailAddress,
} from "./rules-editor-validation";

export interface RulesEditorState {
	input: RulesInputState;
	error: RulesEditorErrorState;
	computedRule: NormalizedRule;
}

type ParseResult<D, E> =
	| {
			success: true;
			result: D;
	  }
	| {
			success: false;
			error: E;
	  };

function tryParseDeviceConfig(
	config: string | null,
): ParseResult<VelavuRuleDeviceConfig | null, void> {
	//Propagate null input
	if (config === null) {
		return {
			success: true,
			result: null,
		};
	}

	//Try to parse the string as JSON
	let parsedOutput: unknown;
	try {
		parsedOutput = JSON.parse(config);
	} catch {
		return {
			success: false,
			error: undefined,
		};
	}

	//Make sure the result is a JSON object
	if (
		typeof parsedOutput !== "object" ||
		Array.isArray(parsedOutput) ||
		parsedOutput === null
	) {
		return {
			success: false,
			error: undefined,
		};
	}

	//Make sure the object is not empty
	if (Object.keys(parsedOutput).length === 0) {
		return {
			success: false,
			error: undefined,
		};
	}

	return {
		success: true,
		result: parsedOutput,
	};
}

function stringToStringArray(input: string): string[] {
	return input
		.split(",")
		.map((item) => item.trim())
		.filter((item) => item.length > 0);
}

function tryParseEvent(
	event: RulesEventInputState | null,
): ParseResult<NormalizedRuleEventOptions | null, RulesEditorEventErrorState> {
	if (event === null) {
		return {
			success: true,
			result: null,
		};
	}

	//Parse the arrays
	const phoneNumbers = stringToStringArray(event.phoneNumbers);
	const emailAddresses = stringToStringArray(event.emailAddresses);

	//Check for formatting errors
	const error: RulesEditorEventErrorState = {
		phoneNumbers: !phoneNumbers.every(isValidE164USPhoneNumber),
		emailAddresses: !emailAddresses.every(isValidEmailAddress),
	};
	if (error.phoneNumbers || error.emailAddresses) {
		return {
			success: false,
			error,
		};
	}

	return {
		success: true,
		result: {
			message: event.message,
			phoneNumbers,
			emailAddresses,
			storeEvent: event.storeEvent,
			storeAlert: event.storeAlert,
		},
	};
}

export default function useRulesEditorState(
	initialRule: VelavuRule | null,
): RulesEditorState {
	//Basic rule fields
	const [name, setName] = useState(initialRule?.name ?? "");
	const [description, setDescription] = useState(
		initialRule?.description ?? "",
	);
	const [enabled, setEnabled] = useState(initialRule?.enabled ?? true);

	const [priority, setPriority] = useState(initialRule?.priority ?? 0);
	const [condition, setCondition] = useState(initialRule?.condition ?? "");

	const [activationEvent, setActivationEvent] =
		useState<RulesEventInputState | null>(() => {
			return rulesEventInputStateFromEventOptions(
				initialRule?.action.on_active,
			);
		});

	const [deactivationEvent, setDeactivationEvent] =
		useState<RulesEventInputState | null>(() => {
			return rulesEventInputStateFromEventOptions(
				initialRule?.action.on_deactive,
			);
		});

	const [deviceConfig, setDeviceConfig] = useState<string | null>(() => {
		if (initialRule?.action?.config !== undefined) {
			return JSON.stringify(initialRule.action.config, null, 2);
		} else {
			return null;
		}
	});

	//Compute rule and error
	const [computedRule, computedError] = useMemo((): [
		NormalizedRule,
		RulesEditorErrorState,
	] => {
		//Initialize the new rule
		const rule: NormalizedRule = {
			name: name,
			description: description,
			enabled: enabled,
			condition: condition,
			priority: priority,
			action: {
				config: null,
				activationEvent: null,
				deactivationEvent: null,
			},
		};
		const error = makeDefaultRulesEditorErrorState();

		//Parse the device config
		const parsedConfigResult = tryParseDeviceConfig(deviceConfig);
		if (parsedConfigResult.success) {
			rule.action.config = parsedConfigResult.result;
		} else {
			error.deviceConfig = true;
		}

		//Parse the activation event
		const parsedActivationEventResult = tryParseEvent(activationEvent);
		if (parsedActivationEventResult.success) {
			rule.action.activationEvent = parsedActivationEventResult.result;
		} else {
			error.activationEvent = parsedActivationEventResult.error;
		}

		//Parse the deactivation event
		const parsedDeactivationEventResult = tryParseEvent(deactivationEvent);
		if (parsedDeactivationEventResult.success) {
			rule.action.deactivationEvent =
				parsedDeactivationEventResult.result;
		} else {
			error.deactivationEvent = parsedDeactivationEventResult.error;
		}

		return [rule, error];
	}, [
		activationEvent,
		condition,
		deactivationEvent,
		description,
		deviceConfig,
		enabled,
		name,
		priority,
	]);

	const inputState = useMemo(
		(): RulesInputState => ({
			name,
			setName,
			description,
			setDescription,
			enabled,
			setEnabled,

			priority,
			setPriority,
			condition,
			setCondition,

			activationEvent,
			setActivationEvent,

			deactivationEvent,
			setDeactivationEvent,

			deviceConfig,
			setDeviceConfig,
		}),
		[
			name,
			setName,
			description,
			setDescription,
			enabled,
			setEnabled,
			priority,
			setPriority,
			condition,
			setCondition,
			activationEvent,
			setActivationEvent,
			deactivationEvent,
			setDeactivationEvent,
			deviceConfig,
			setDeviceConfig,
		],
	);

	return useMemo(
		(): RulesEditorState => ({
			input: inputState,
			error: computedError,
			computedRule: computedRule,
		}),
		[inputState, computedError, computedRule],
	);
}
